import { createNoise2D } from 'simplex-noise';
import type { Rng } from './rng.js';

export interface FractalNoise {
  (x: number, y: number): number;
}

/**
 * Fractal Brownian motion — sums multiple octaves of simplex noise.
 * Returns values roughly in [-1, 1].
 */
export function makeFractalNoise(
  rng: Rng,
  options: {
    octaves?: number;
    persistence?: number;
    lacunarity?: number;
    frequency?: number;
  } = {},
): FractalNoise {
  const octaves = options.octaves ?? 5;
  const persistence = options.persistence ?? 0.5;
  const lacunarity = options.lacunarity ?? 2.0;
  const baseFreq = options.frequency ?? 1;

  const noises: ReturnType<typeof createNoise2D>[] = [];
  for (let i = 0; i < octaves; i++) {
    noises.push(createNoise2D(rng));
  }

  return (x: number, y: number) => {
    let amplitude = 1;
    let frequency = baseFreq;
    let sum = 0;
    let max = 0;
    for (let i = 0; i < octaves; i++) {
      sum += noises[i]!(x * frequency, y * frequency) * amplitude;
      max += amplitude;
      amplitude *= persistence;
      frequency *= lacunarity;
    }
    return sum / max;
  };
}

/** Radial falloff so the world looks like a continent surrounded by sea. */
export function radialFalloff(
  x: number,
  y: number,
  width: number,
  height: number,
  strength = 1,
): number {
  const cx = width / 2;
  const cy = height / 2;
  const dx = (x - cx) / (width / 2);
  const dy = (y - cy) / (height / 2);
  const d = Math.sqrt(dx * dx + dy * dy);
  // smoothstep-ish: 0 in center, 1 at edges, with soft transition
  const t = Math.min(1, Math.max(0, d));
  return strength * t * t * (3 - 2 * t);
}
